import type { CredentialType } from '@seek/ca-myprofile-utils';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as createGuid } from 'uuid';

import type { DirtyFormProps } from 'src/components/FormDrawer/FormDrawer';
import { useEnterReadMode } from 'src/components/Router/useEnterReadMode';
import { useId } from 'src/components/Router/useId';
import { usePage } from 'src/components/Router/usePage';
import { EDIT, ADD } from 'src/consts';
import { certsyLinkClicked } from 'src/modules/analytics/certsy/actionCreators';
import type { AnalyticsActionOrigin } from 'src/modules/analytics/types';
import { useAppConfig } from 'src/modules/appConfig';
import { parseUrl } from 'src/modules/utils';
import type { ActionName } from 'src/types';

import {
  useLicences,
  useRemoveLicence,
  useUpdateLicence,
  useUpdateVerifiedLicence,
} from '../data';
import type { LicenceUpdate } from '../types';
import {
  graphQLLicenceToUILicence,
  licenceUpdateToUpdateLicenceInput,
} from '../utils';

import { LicenceFormRenderer as LicenceForm } from './LicenceFormRenderer';

export const EditLicences = ({ onFormDirtyChange }: DirtyFormProps) => {
  const dispatch = useDispatch();
  const enterReadMode = useEnterReadMode();

  const { locale, language: languageCode, zone } = useAppConfig();

  const { licences, loading } = useLicences();

  const [dataLoadedFromUrl, setDataLoadedFromUrl] = useState(false);

  const paramId = useId();
  const activeLicence = licences.find((licence) => licence.id === paramId);

  const licence = activeLicence
    ? graphQLLicenceToUILicence(activeLicence)
    : undefined;

  const [actionName] = useState<ActionName>(licence ? EDIT : ADD);

  const isVerifiedLicence = typeof licence?.credential !== 'undefined';

  const urlParams = parseUrl(window.location);

  const [removeLicence] = useRemoveLicence();
  const deleteLicence = useCallback(() => {
    if (licence) {
      enterReadMode();
      removeLicence({
        deleteLicenceInput: { id: licence.id },
        languageCode,
        zone,
      });
    }
  }, [enterReadMode, languageCode, licence, removeLicence, zone]);

  const onCertsySharingOpen = useCallback(
    (credentialType: CredentialType) => () => {
      window.open(
        activeLicence?.credential?.deleteVerificationUrl,
        '_blank',
        'noopener,noreferrer',
      );
      dispatch(
        certsyLinkClicked({
          linkUrl: activeLicence?.credential?.deleteVerificationUrl,
          linkAction: 'delete',
          linkPosition: 'pop up',
          credentialType,
          currentPageSection: 'licence',
        }),
      );
    },
    [activeLicence, dispatch],
  );

  const [updateVerifiedLicence] = useUpdateVerifiedLicence();
  const submitVerifiedForm = useCallback(
    (params: { dirty: boolean; values: { description: string } }): void => {
      if (licence) {
        const { dirty, values } = params;
        if (dirty) {
          updateVerifiedLicence({
            verifiedLicenceData: { ...values, id: licence.id },
          });
        }
        enterReadMode();
      }
    },
    [enterReadMode, licence, updateVerifiedLicence],
  );

  const [updateLicence] = useUpdateLicence(actionName);

  const submitForm = useCallback(
    ({ dirty, values }: { dirty: boolean; values: LicenceUpdate }): void => {
      if (dirty || dataLoadedFromUrl) {
        const licenceId = licence ? licence.id : createGuid();
        const licenceData = licenceUpdateToUpdateLicenceInput({
          ...values,
          id: licenceId,
        });
        const analytics =
          actionName === 'ADD' &&
          dataLoadedFromUrl &&
          urlParams?.profileDataSource
            ? {
                actionOrigin:
                  urlParams.profileDataSource as AnalyticsActionOrigin,
              }
            : {};
        updateLicence({
          licenceData,
          ...analytics,
          languageCode,
          zone,
        });
      }
      enterReadMode();
    },
    [
      actionName,
      dataLoadedFromUrl,
      enterReadMode,
      languageCode,
      licence,
      updateLicence,
      urlParams.profileDataSource,
      zone,
    ],
  );

  const onManageClick = useCallback(() => {
    if (activeLicence?.credentialType) {
      dispatch(
        certsyLinkClicked({
          linkUrl: activeLicence?.credential?.manageVerificationUrl,
          linkAction: 'manage',
          linkPosition: 'main card',
          credentialType: activeLicence.credentialType,
          currentPageSection: 'licence',
        }),
      );
    }
  }, [activeLicence, dispatch]);

  const onCertsyClick = useCallback(() => {
    if (activeLicence?.credentialType) {
      dispatch(
        certsyLinkClicked({
          linkUrl: activeLicence?.credential?.manageVerificationUrl,
          linkAction: 'manage',
          linkPosition: 'pop up',
          credentialType: activeLicence.credentialType,
          currentPageSection: 'licence',
        }),
      );
    }
  }, [activeLicence, dispatch]);

  const page = usePage();

  useEffect(
    () => () => {
      if (page === 'profile-page') {
        // Closing a card will trigger a history.go(-1)
        // This timeout ensures that the history call executes first
        // before clearing query params from the url
        setTimeout(() => {
          window.history.replaceState(
            null,
            document.title,
            window.location.pathname,
          );
        });
      }
    },
    [page],
  );

  const allowDelete = actionName === EDIT;

  const onDelete =
    isVerifiedLicence && activeLicence?.credentialType
      ? onCertsySharingOpen(activeLicence.credentialType)
      : deleteLicence;

  if (loading) {
    return null;
  }

  return (
    <LicenceForm
      activeLicence={licence}
      dataLoadedFromUrl={dataLoadedFromUrl}
      isVerifiedLicence={isVerifiedLicence}
      locale={locale}
      actionName={actionName}
      onFormDirtyChange={onFormDirtyChange}
      submitForm={submitForm}
      submitVerifiedForm={submitVerifiedForm}
      onCancel={enterReadMode}
      urlParams={urlParams}
      validateOnMount={false}
      setDataLoadedFromUrl={setDataLoadedFromUrl}
      onManageClick={onManageClick}
      onCertsyClick={onCertsyClick}
      {...(allowDelete && { onDelete })}
    />
  );
};
